/**
 * Always-isolated Playwright trace archive merging.
 *
 * Unlike the snapshot wrappers, this path is not feature-gated and never falls
 * back inline: `adm-zip` performs synchronous inflate/deflate work that can
 * monopolize the host event loop. Each merge gets a fresh, single-use child so
 * large archive buffers are reclaimed immediately after the call.
 *
 * @module cpu-offload/trace-merge
 */

import { randomUUID } from 'node:crypto';
import { rename, rm } from 'node:fs/promises';
import { dirname, join } from 'node:path';
import type { MergeTraceResult } from '@chatsdet/trace-tools';
import { withExpensiveSlot } from '../expensive-gate.js';
import { type BrowserLogger, consoleLogger } from '../logger.js';
import { CpuOffloadPool, type CpuOffloadPoolOptions } from './pool.js';

export const DEFAULT_TRACE_MERGE_TIMEOUT_MS = 60_000;
export const DEFAULT_TRACE_MERGE_RSS_BYTES = 4 * 1024 * 1024 * 1024;

type TraceMergeIsolationOptions = {
  timeoutMs?: number;
  rssBytes?: number;
  rssCheckIntervalMs?: number;
  workerPath?: string;
  readRssBytes?: CpuOffloadPoolOptions['readRssBytes'];
  logger?: BrowserLogger;
  /** @internal Test-only task override for deterministic timeout/crash fixtures. */
  task?: string;
};

const removePartial = async (partialPath: string, logger: BrowserLogger): Promise<void> => {
  try {
    await rm(partialPath, { force: true });
  } catch (error) {
    logger.warn('[TraceMerge] Failed to remove partial archive', {
      partialPath,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

/**
 * Merge trace archives in a supervised, one-use Bun child.
 *
 * The child is bounded by both a wall-clock deadline and an RSS ceiling. Any
 * breach kills only that child and rejects this call; the merge is never retried
 * on the host event loop. Output is promoted atomically from a sibling partial
 * file only after the child reports success.
 */
export async function mergeTraceArchivesIsolated(
  zipPaths: string[],
  outPath: string,
  options: TraceMergeIsolationOptions = {}
): Promise<MergeTraceResult> {
  const logger = options.logger ?? consoleLogger;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TRACE_MERGE_TIMEOUT_MS;
  const partialPath = join(dirname(outPath), `.trace-merge-${randomUUID()}.partial`);
  const pool = new CpuOffloadPool({
    size: 1,
    defaultTimeoutMs: timeoutMs,
    rssBytes: options.rssBytes ?? DEFAULT_TRACE_MERGE_RSS_BYTES,
    ...(options.rssCheckIntervalMs ? { rssCheckIntervalMs: options.rssCheckIntervalMs } : {}),
    ...(options.workerPath ? { workerPath: options.workerPath } : {}),
    ...(options.readRssBytes ? { readRssBytes: options.readRssBytes } : {}),
    logger,
  });

  try {
    const result = await withExpensiveSlot(() =>
      pool.run<MergeTraceResult>(
        options.task ?? 'merge-trace-archives',
        { zipPaths, outPath: partialPath },
        { timeoutMs }
      )
    );
    await rename(partialPath, outPath);
    return { ...result, path: outPath };
  } finally {
    await pool.shutdown();
    await removePartial(partialPath, logger);
  }
}
